import { Token } from 'config/types'
import useFullSizeModalRouteUpdater from 'hooks/useFullSizeModalRouteUpdater'
import useModal from 'hooks/useModal'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { ModalBody, ModalHeader } from 'UIKit/AlchemyModal'
import { ModalSizeModeEnum } from 'UIKit/AlchemyModal/Modal'
import { ModalProps } from 'UIKit/ModalV2'
import Text from 'UIKit/Text'
import TokenLogo from 'UIKit/TokenLogo'
import { useWaitingDepositToActiveBonus } from 'views/Payment/components/Deposit/DepositCryptos/hooks'
import { PaymentPageEnums } from 'views/Payment/Menu'
import { PaymentHistoryButton } from '../../components/PaymentHistoryModal'
import PaymentTokenSelectModal from '../../components/PaymentTokenSelectModal'
import SelectNetworkByCurrencyModal from '../../components/SelectNetworkByCurrencyModal'
import { PAYMENT_MODAL_THEME, StylePaymentModal } from '../../components/styles'
import { useTokensWithSameCode } from '../../hooks/useNetwork'
import { useControlSelectToken } from '../../hooks/useTokens'
import Swap from '../../Swap'
import DepositForm from './DepositForm'

const DepositCrytosModal: React.FC<
  ModalProps<{
    selectedToken: Token
    onSelect: (token: Token) => void
    isDepositing: boolean
    onDepositing: (isDepositing) => void
    autoOpenSelectNetwork?: boolean
  }>
> = ({
  onDismiss,
  data: { onSelect, selectedToken: selectedTokenProps, isDepositing, onDepositing, autoOpenSelectNetwork },
}) => {
  const { t } = useTranslation()
  const onBack = useFullSizeModalRouteUpdater(`DC-${selectedTokenProps.name}`, onDismiss)
  const bonusControl = useWaitingDepositToActiveBonus()
  const { selectedToken, selectedNetwork, onSelectNetworkByToken } = useControlSelectToken({
    initialToken: selectedTokenProps,
    onSelectToken: onSelect,
  })

  const [onPresent] = useModal(SelectNetworkByCurrencyModal)
  const [onPresentSwap] = useModal(Swap)
  const sameCodeTokens = useTokensWithSameCode(selectedToken, PaymentPageEnums.Deposit)

  const handleClose = () => {
    onSelect(null)
    onBack()
  }

  useEffect(() => {
    if (autoOpenSelectNetwork) {
      if (sameCodeTokens.length > 1) {
        setTimeout(() => {
          onPresent({
            selectedToken,
            selectedNetwork,
            onSelectToken: onSelectNetworkByToken,
            from: PaymentPageEnums.Deposit,
          })
        }, 500)
      } else if (sameCodeTokens.length === 1) {
        onSelectNetworkByToken(selectedToken)
      }
    }
  }, [autoOpenSelectNetwork])

  return (
    <CustomModal sizeMode={ModalSizeModeEnum.Full} colorTheme={PAYMENT_MODAL_THEME} onDismiss={handleClose}>
      <ModalHeader
        useBackIcon
        hideClose
        hideStroke
        colorTheme={PAYMENT_MODAL_THEME}
        titleAlign="center"
        onBack={handleClose}
        title={
          <Text
            width="fit-content"
            display="inline-flex"
            style={{
              gap: '8px',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
            fontWeight={600}
            color="currentColor"
          >
            <TokenLogo token={selectedToken} hideNetwork size={20} />
            {t('Deposit')} {selectedToken.name}
          </Text>
        }
        suffixNode={<PaymentHistoryButton from={PaymentPageEnums.Deposit} />}
      />
      <ModalBody>
        <DepositForm
          height="100%"
          selectedNetwork={selectedNetwork}
          selectedToken={selectedToken}
          onSelectToken={onSelectNetworkByToken}
          isDepositing={isDepositing}
          onChangeDepositing={onDepositing}
          bonusControl={bonusControl}
          onRedirectToSwap={onPresentSwap}
        />
      </ModalBody>
    </CustomModal>
  )
}

const DepositCrytoes: React.FC<
  ModalProps<{
    initialToken?: Token
    isFiatCurrency?: boolean
  }>
> = ({ data, onDismiss }) => {
  const [isDepositing, setIsDepositing] = useState(false)
  const { selectedToken, onSelectToken } = useControlSelectToken({
    initialToken: data.initialToken,
  })

  const [onPresent] = useModal(DepositCrytosModal)
  const selectToken = useCallback(
    (token: Token) => {
      onSelectToken(token)
      if (!token) return
      onPresent({
        isDepositing,
        onDepositing: setIsDepositing,
        onSelect: onSelectToken,
        selectedToken: token,
        autoOpenSelectNetwork: true,
      })
    },
    [isDepositing],
  )

  return (
    <PaymentTokenSelectModal
      data={{
        selectedToken,
        onCurrencySelect: selectToken,
        from: PaymentPageEnums.Deposit,
        hideHUSDToken: true,
      }}
      onDismiss={() => {
        onDismiss()
        selectToken(null)
      }}
    />
  )
}

const CustomModal = styled(StylePaymentModal)`
  .wrap-action-header {
    max-height: 21px;
  }
`

export default DepositCrytoes
